import { createSelector } from 'reselect';
import {
  pathOr,
  identity,
  memoizeWith,
  path,
  prop,
  propEq,
  find,
  compose,
  values,
  filter,
  always,
  T,
  equals,
  cond,
  sortBy,
  findLast, propOr,
} from 'ramda';

import { isNotNil } from 'ramda-extension';
import * as messenger from '../../constants/messenger';
import { getUsersEntities } from '../users/selectors';
import { isHasMemberId } from '../../utils/helpers/mentionHelpers/lookup';
import { STATUS } from '../../constants/ui';
import { sortUserByOnlineStatus } from '../../utils/helpers/userHelpers/status';
import { getChannel } from '../../utils/helpers/messengerHelpers/channelHelpers';

const messagesSelector = createSelector(
  state => path(['messenger', 'messages'], state),
  identity,
);

const channelsSelector = createSelector(
  state => pathOr({}, ['messenger', 'channels'], state),
  identity,
);


const getMessages = createSelector(
  messagesSelector,
  messages => memoizeWith(identity, channelId => propOr({}, 'entities', messages[channelId])),
);

const getAllRenderedMessages = createSelector(
  messagesSelector,
  messages => memoizeWith(identity, channelId => prop('allRenderedMessages', messages[channelId])),
);

const getMessage = createSelector(
  messagesSelector,
  messages => memoizeWith(identity, (messageId, channelId) => pathOr(null, [channelId, 'entities', 'messages', messageId], messages)),
);

const getParentMessage = createSelector(
  messagesSelector,
  getMessage,
  (messagesList, message) => memoizeWith(
    identity,
    (messageId, channelId) => {
      const messageEntity = message(messageId, channelId);
      const parentId = pathOr(null, ['parent', 'ts'], messageEntity);
      return pathOr(
        null,
        [prop('channel_id', messageEntity), 'entities', 'messages', parentId],
        messagesList,
      );
    },
  ),
);

const getMessageList = createSelector(
  messagesSelector,
  messages => memoizeWith(identity, channelId => prop('result', messages[channelId])),
);

const getMessageDays = createSelector(
  messagesSelector,
  messages => memoizeWith(identity, channelId => pathOr({}, ['timestamps'], messages[channelId])),
);

const getCountLoadedMessages = createSelector(
  messagesSelector,
  messages => memoizeWith(identity, channelId => pathOr(0, ['count'], messages[channelId])),
);

const getHasMoreMessages = createSelector(
  messagesSelector,
  messages => memoizeWith(identity, channelId => prop('hasMore', messages[channelId])),
);

const getChannelById = createSelector(
  channelsSelector,
  channels => memoizeWith(identity, channelId => getChannel(channelId)(channels)),
);

const getLoadedChannels = createSelector(
  state => path(['messenger', 'loadedChannels'], state),
  loadedChannels => memoizeWith(identity, channelId => pathOr(null, [channelId], loadedChannels)),
);

const getLastMessageTime = createSelector(
  messagesSelector,
  messages => memoizeWith(identity, channelId => pathOr(null, ['lastMessageTime'], messages[channelId])),
);

const getActiveChannel = createSelector(pathOr(null, ['messenger', 'activeChannel']), identity);

const getGroupChannels = createSelector(
  pathOr({}, ['messenger', 'channels', 'groupChannels', 'entities']),
  identity,
);

const getGroupChannelsList = createSelector(
  pathOr([], ['messenger', 'channels', 'groupChannels', 'result']),
  identity,
);

const getGroupChannelsCount = createSelector(
  pathOr(0, ['messenger', 'channels', 'groupChannels', 'count']),
  identity,
);

const getDirectChannels = createSelector(
  pathOr(false, ['messenger', 'channels', 'directChannels', 'entities']),
  identity,
);

const getDirectChannelsList = createSelector(
  pathOr([], ['messenger', 'channels', 'directChannels', 'result']),
  identity,
);

const getDirectChannelsCount = createSelector(
  pathOr(0, ['messenger', 'channels', 'directChannels', 'count']),
  identity,
);

const getMatchedDirectChannelsList = createSelector(
  pathOr([], ['messenger', 'channels', 'directChannels', 'matched']),
  identity,
);

const getMatchedDirectChannelsCount = createSelector(
  pathOr(0, ['messenger', 'channels', 'directChannels', 'matchedCount']),
  identity,
);

const getYouCanJoinChannelsList = createSelector(
  pathOr([], ['messenger', 'channels', 'groupChannels', 'youCanJoinChannels']),
  identity,
);

const getYouCanJoinChannelsCount = createSelector(
  pathOr(0, ['messenger', 'channels', 'groupChannels', 'youCanJoinChannelsCount']),
  identity,
);

const getYouCanJoinDirectChannelsList = createSelector(
  pathOr([], ['messenger', 'channels', 'directChannels', 'youCanJoinDirectChannelsList']),
  identity,
);

const getYouCanJoinDirectChannelsCount = createSelector(
  pathOr(0, ['messenger', 'channels', 'directChannels', 'youCanJoinDirectChannelsCount']),
  identity,
);


const getYouBelongToChannelsList = createSelector(
  pathOr([], ['messenger', 'channels', 'groupChannels', 'youBelongToChannels']),
  identity,
);

const getYouBelongToChannelsCount = createSelector(
  pathOr(0, ['messenger', 'channels', 'groupChannels', 'youBelongToChannelsCount']),
  identity,
);

const getDirectChannelId = createSelector(
  getDirectChannels,
  channels => memoizeWith(identity, userId => compose(
    prop('id'),
    find(propEq('user_id', userId)),
    values,
  )(channels)),
);

const getActiveChannelEntity = createSelector(
  (state) => {
    if (!getActiveChannel(state)) return {};
    const { type, id } = getActiveChannel(state);
    if (type === messenger.GROUP_CHANNEL
      || type === messenger.PRIVATE_CHANNEL
      || type === messenger.GENERAL_CHANNEL) {
      return pathOr({}, ['messenger', 'channels', 'groupChannels', 'entities', id], state);
    }
    return pathOr({}, ['messenger', 'channels', 'directChannels', 'entities', id], state);
  },
  identity,
);

const getMembersEntities = createSelector(
  pathOr({}, ['messenger', 'channels', 'groupChannels', 'members']),
  identity,
);

const getMembers = createSelector(
  [getActiveChannelEntity, getUsersEntities],
  (channel, users) => {
    const members = prop('members', channel);
    const getMembersData = compose(
      sortUserByOnlineStatus(STATUS.ONLINE),
      filter(isHasMemberId(members)),
    );
    return cond([
      [isNotNil, () => getMembersData(users)],
      [T, always(null)],
    ])(members);
  },
);

const getActiveChannelMembers = createSelector(
  getActiveChannelEntity,
  pathOr([], ['members']),
);

const getPinnedMessages = createSelector(
  state => path(['messenger', 'pinnedMessages'], state),
  pinnedMessages => memoizeWith(identity, channelId => pathOr({}, [channelId, 'entities'], pinnedMessages)),
);

const getPinnedMessagesId = createSelector(
  state => path(['messenger', 'pinnedMessages'], state),
  pinnedMessages => memoizeWith(identity, channelId => pathOr([], [channelId, 'result'], pinnedMessages)),
);

const getLastMessageId = createSelector(
  messagesSelector,
  messages => memoizeWith(identity, channelId => pathOr(null, ['lastMessageId'], messages[channelId])),
);

const getLastUserMessage = createSelector(
  messagesSelector,
  messages => memoizeWith(identity, (channelId, userId) => compose(
    findLast(propEq('created_by', userId)),
    sortBy(prop('ts')),
    values,
    pathOr(null, ['entities', 'messages']),
  )(messages[channelId])),
);

const getLastUserOwnerMessage = createSelector(
  messagesSelector,
  messages => memoizeWith(identity, channelId => pathOr(null, ['lastUserOwnerMessage'], messages[channelId])),
);

const joinToChannelPending = createSelector(
  pathOr(false, ['pending', 'joinToChannelRequest']),
  identity,
);

const submitMessageRequest = createSelector(
  pathOr(false, ['pending', 'submitMessageRequest']),
  identity,
);

const addMembersToChannelPending = createSelector(
  pathOr(false, ['pending', 'addMemberToChannelRequest']),
  identity,
);
const deleteMemberFromChannelPending = createSelector(
  pathOr(false, ['pending', 'deleteMemberFromChannelRequest']),
  identity,
);

const getGroupChannelPending = createSelector(
  pathOr(true, ['pending', 'getChannelRequest']),
  identity,
);

const addChannelPending = createSelector(
  pathOr(false, ['pending', 'setNewChannel']),
  identity,
);

const getIsDeleteMessagePending = createSelector(
  pathOr(false, ['pending', 'deleteMessageRequest']),
  identity,
);

const getMessagesRequest = createSelector(
  pathOr(true, ['pending', 'getMessagesRequest']),
  identity,
);

const getScrollToMessage = createSelector(
  pathOr(null, ['messenger', 'scrollToMessage']),
  identity,
);

const getMember = createSelector(
  state => memoizeWith(identity, memberId => pathOr({}, [
    'messenger',
    'channels',
    'groupChannels',
    'members',
    memberId,
  ], state)), identity,
);

const getIsUserOwnsChannel = createSelector(
  pathOr(null, ['messenger', 'channels', 'groupChannels', 'entities']),
  channels => memoizeWith(identity, channelId => memoizeWith(identity, userId => equals(path([channelId, 'created_by'], channels), userId))),
);
const getMuteChannelsList = createSelector(pathOr([], ['messenger', 'muteChannels']), identity);

const getTotalUnreadCount = createSelector(
  pathOr(0, ['messenger', 'totalUnreadCount']),
  identity,
);

const getMoreMessagesPending = createSelector(pathOr(false, ['pending', 'getMessagesMoreRequest']), identity);

const getSearchMessagesEntities = createSelector(
  pathOr({}, ['messenger', 'searchMessages', 'entities']),
  identity,
);

const getSearchMessagesResult = createSelector(
  pathOr([], ['messenger', 'searchMessages', 'result']),
  identity,
);

const getSearchMessagesHasMore = createSelector(
  pathOr(false, ['messenger', 'searchMessages', 'hasMore']),
  identity,
);

const getSearchMessagesCount = createSelector(
  pathOr(0, ['messenger', 'searchMessages', 'count']),
  identity,
);

const getPendingSearchMessages = createSelector(
  pathOr(false, ['pending', 'searchMessagesRequest']),
  identity,
);


export {
  getSearchMessagesEntities,
  getSearchMessagesResult,
  getSearchMessagesHasMore,
  getSearchMessagesCount,
  getGroupChannels,
  getMessageDays,
  getGroupChannelsList,
  getIsDeleteMessagePending,
  getMessages,
  getLoadedChannels,
  getMessageList,
  getCountLoadedMessages,
  getDirectChannels,
  getDirectChannelsList,
  getIsUserOwnsChannel,
  getDirectChannelsCount,
  getMatchedDirectChannelsList,
  getMatchedDirectChannelsCount,
  getYouBelongToChannelsList,
  getYouBelongToChannelsCount,
  getYouCanJoinChannelsList,
  getYouCanJoinChannelsCount,
  submitMessageRequest,
  getHasMoreMessages,
  getActiveChannelEntity,
  channelsSelector,
  getActiveChannel,
  getMembers,
  getChannelById,
  getLastMessageTime,
  getDirectChannelId,
  getMessage,
  getActiveChannelMembers,
  getPinnedMessages,
  getPinnedMessagesId,
  getLastMessageId,
  getLastUserMessage,
  getLastUserOwnerMessage,
  joinToChannelPending,
  addChannelPending,
  addMembersToChannelPending,
  getMessagesRequest,
  deleteMemberFromChannelPending,
  getMembersEntities,
  getMember,
  getGroupChannelsCount,
  getGroupChannelPending,
  getMuteChannelsList,
  getTotalUnreadCount,
  getParentMessage,
  getAllRenderedMessages,
  getMoreMessagesPending,
  getScrollToMessage,
  getYouCanJoinDirectChannelsList,
  getYouCanJoinDirectChannelsCount,
  getPendingSearchMessages,
};

export default messagesSelector;
